import type { CSSProperties, MouseEvent, ReactNode } from "react";
import * as Haptics from "@/shared/platform/haptics";
import {
  buttonSize,
  colors,
  rawColors,
  fontWeight,
  radius,
  shadow,
  typography,
} from "../design-system";
import { AppPressable } from "./AppPressable";
import { AppText } from "./AppText";
import { Spinner } from "./Spinner";

type ButtonVariant = "primary" | "ghost" | "sos" | "dark";

type ButtonProps = {
  title: string;
  variant?: ButtonVariant;
  size?: "md" | "lg";
  left?: ReactNode;
  right?: ReactNode;
  style?: CSSProperties;
  className?: string;
  /**
   * Pendant une mutation : spinner à la place de `left`, clics ignorés.
   * Le libellé RESTE affiché (règle Base : la largeur du bouton ne change pas,
   * l'utilisateur sait toujours sur quoi il avait appuyé).
   */
  loading?: boolean;
  /** Désactivé : la hiérarchie s'effondre en un seul état gris, clics ignorés. */
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit";
};

const base: CSSProperties = {
  height: buttonSize.lg, // 56 — token, plus un littéral
  borderRadius: radius.btn, // 14px — jamais pill sur les boutons
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  gap: 8,
  paddingLeft: 18,
  paddingRight: 18,
  transition: "transform 120ms, opacity 120ms",
};

const variants: Record<ButtonVariant, CSSProperties> = {
  primary: { backgroundColor: colors.or500, boxShadow: shadow.sh1 },
  ghost: { backgroundColor: colors.gray100 },
  sos: { backgroundColor: colors.sos600, boxShadow: shadow.sos },
  dark: { backgroundColor: colors.black },
};

// Disabled : toutes les variantes s'effondrent dans le MÊME gris (règle Base) —
// un bouton inactif n'a plus de hiérarchie, et l'ombre disparaît (une surface
// inerte ne « flotte » pas). `elevation` d'Android n'a plus lieu d'être :
// annuler `boxShadow` suffit.
const disabledStyle: CSSProperties = {
  backgroundColor: colors.gray100,
  boxShadow: "none",
};

const pressedStyle: CSSProperties = { transform: "scale(0.98)", opacity: 0.92 };

/**
 * CTA de l'app.
 *
 * ## Couleur du libellé selon le FOND
 *   ghost (gris clair) → noir · sos/dark (fonds sombres) → blanc
 *   primary (or clair) → NAVY, car le blanc y serait illisible (2.4:1 contre
 *   7.9:1 pour le navy). L'or est une couleur claire : elle exige un libellé
 *   sombre, contrairement à l'ancien orange.
 *
 * ## Note web
 * `type="button"` par défaut est délibéré : dans un `<form>`, le défaut HTML est
 * `submit`, et tout bouton secondaire soumettrait le formulaire par accident.
 * Passer `type="submit"` explicitement pour le bouton de validation — cela
 * apporte au passage la soumission par la touche Entrée, que le natif n'avait pas.
 */
export function Button({
  title,
  variant = "primary",
  size = "lg",
  left,
  right,
  style,
  className,
  loading = false,
  disabled = false,
  onClick,
  type = "button",
  ...props
}: ButtonProps) {
  // Un bouton en cours de chargement est inerte SANS paraître désactivé : le
  // gris « disabled » sur un CTA pendant sa propre mutation se lirait comme un
  // échec. Les deux états coupent clics ET haptique.
  const inert = loading || disabled;

  const labelColor = disabled
    ? colors.gray500
    : variant === "ghost"
      ? colors.black
      : variant === "sos" || variant === "dark"
        ? colors.white
        : rawColors.navy800;

  return (
    <AppPressable
      {...props}
      type={type}
      className={className}
      aria-busy={loading}
      disabled={inert}
      onClick={(event) => {
        if (inert) return;
        Haptics.selectionAsync().catch(() => undefined);
        onClick?.(event);
      }}
      style={{
        ...base,
        ...(size === "md"
          ? { height: buttonSize.md, paddingLeft: 14, paddingRight: 14, gap: 6 }
          : null),
        ...variants[variant],
        ...(disabled ? disabledStyle : null),
        ...style,
      }}
      pressedStyle={inert ? undefined : pressedStyle}
    >
      {loading ? <Spinner size={18} color={labelColor} /> : left}
      <AppText
        variant="button"
        color={labelColor}
        style={
          size === "md"
            ? { ...typography.label, fontWeight: fontWeight.semiBold }
            : undefined
        }
      >
        {title}
      </AppText>
      {!loading && right}
    </AppPressable>
  );
}
